/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';

const initialState = {
  fieldsWithExpressions: {},
  fieldsToWatch: {},
  repeatGroupList: {},
  groupList: {},
  repeatGroupFields: {},
  subforms: {},
  subformFields: {},
  metadataInputs: {},
  runningProcesses: [],
  selectResourceOptions: {},
  selectFieldResources: {},
  rules: {},
  initialAnswers: {},
  fieldErrors: {},
  fieldResourceUpdateHighlights: {},
  requiredResourcesLoading: 0,
  draftAnswers: {},
  dispatchAnswers: {},
  resourceVersions: {},
  rootLevelFields: {},
  isLoading: true,
  isSubmitting: false,
};

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    resetFormSliceState: () => initialState,
    setFieldsWithExpressions(state, action) {
      state.fieldsWithExpressions = action.payload;
    },
    setFieldsToWatch(state, action) {
      state.fieldsToWatch = action.payload;
    },
    clearFieldsToWatch(state) {
      state.fieldsToWatch = [];
    },
    addRepeatGroup(state, action) {
      const { identifier } = action.payload;

      state.repeatGroupList[identifier] = true;
    },
    addRepeatGroupBulk(state, action) {
      const updatedState = { ...state };

      action.payload.forEach(({ identifier }) => {
        updatedState.repeatGroupList[identifier] = true;
      });
      state = updatedState;
    },
    addGroupToList(state, action) {
      const { identifier, suffix } = action.payload;

      const identifierWithoutSuffixes = getIdWithoutSuffix(identifier);

      if (!state.groupList[identifierWithoutSuffixes]) {
        state.groupList[identifierWithoutSuffixes] = [];
      }

      state.groupList[identifierWithoutSuffixes] = [
        ...state.groupList[identifierWithoutSuffixes],
        `${identifier}|${suffix}`,
      ];
    },
    removeGroupFromList(state, action) {
      const { identifier, suffix } = action.payload;

      const identifierWithoutSuffixes = getIdWithoutSuffix(identifier);

      if (suffix === '') {
        state.groupList[identifierWithoutSuffixes] = state.groupList[
          identifierWithoutSuffixes
        ].filter((id) => !id.includes(`${identifier}`));
      }

      if (state.groupList[identifierWithoutSuffixes]) {
        state.groupList[identifierWithoutSuffixes] = state.groupList[
          identifierWithoutSuffixes
        ].filter((id) => id !== `${identifier}|${suffix}`);

        if (state.groupList[identifierWithoutSuffixes].length === 0) {
          delete state.groupList[identifierWithoutSuffixes];
        }
      }
    },
    addRepeatGroupFields(state, action) {
      const { updateRepeatGroupFields } = action.payload;
      let newRepeatGroupFieldsState = { ...state.repeatGroupFields };

      updateRepeatGroupFields.forEach((identifier) => {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (!newRepeatGroupFieldsState[identifierWithoutSuffix]) {
          newRepeatGroupFieldsState[identifierWithoutSuffix] = [];
        }

        newRepeatGroupFieldsState[identifierWithoutSuffix] = [
          ...newRepeatGroupFieldsState[identifierWithoutSuffix],
          identifier,
        ];
      });

      state.repeatGroupFields = newRepeatGroupFieldsState;
    },
    removeRepeatGroupField(state, action) {
      /**You can either delete fields by identifier or array of identifiers */
      const { identifier, arrayOfIdentifiers = [] } = action.payload;

      if (arrayOfIdentifiers.length > 0) {
        arrayOfIdentifiers.forEach((identifier) => {
          removeGroupField(identifier);
        });
      } else {
        removeGroupField(identifier);
      }

      function removeGroupField(identifier) {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (state.repeatGroupFields[identifierWithoutSuffix]) {
          state.repeatGroupFields[identifierWithoutSuffix] =
            state.repeatGroupFields[identifierWithoutSuffix].filter(
              (id) => id !== identifier
            );

          if (state.repeatGroupFields[identifierWithoutSuffix].length === 0) {
            delete state.repeatGroupFields[identifierWithoutSuffix];
          }
        }
      }
    },
    addSubform(state, action) {
      const { updateSubform } = action.payload;
      let newSubformState = { ...state.subforms };

      updateSubform.forEach((identifier) => {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (!newSubformState[identifierWithoutSuffix]) {
          newSubformState[identifierWithoutSuffix] = [];
        }

        newSubformState[identifierWithoutSuffix] = [
          ...newSubformState[identifierWithoutSuffix],
          identifier,
        ];
      });

      state.subforms = newSubformState;
    },
    addSubformBulk(state, action) {
      const updatedState = { ...state };

      action.payload.forEach(({ identifier }) => {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (!updatedState.subforms[identifierWithoutSuffix]) {
          updatedState.subforms[identifierWithoutSuffix] = [];
        }

        updatedState.subforms[identifierWithoutSuffix] = [
          ...updatedState.subforms[identifierWithoutSuffix],
          identifier,
        ];
      });

      state = updatedState;
    },
    removeSubform(state, action) {
      const { identifier } = action.payload;

      const identifierWithoutSuffix = getIdWithoutSuffix(identifier);
      const partsToRemove = identifier.split('/');

      if (state.subforms[identifierWithoutSuffix]) {
        state.subforms[identifierWithoutSuffix] = state.subforms[
          identifierWithoutSuffix
        ].filter((item) => {
          const itemParts = item.split('/');
          if (itemParts.length === partsToRemove.length) {
            let check = 0;
            for (let i = 0; i < itemParts.length - 1; i++) {
              let itemPartWithoutSuffix = itemParts[i].replaceAll(
                /\|(\d+)?/g,
                ''
              );
              if (itemParts[i] === partsToRemove[i]) {
                check = check + 1;
              } else if (itemPartWithoutSuffix === partsToRemove[i] && i > 0) {
                check = check + 1;
              }
            }
            return !(check === partsToRemove.length - 1);
          }
          return true;
        });
        if (state.subforms[identifierWithoutSuffix].length === 0) {
          delete state.subforms[identifierWithoutSuffix];
        }
      }
    },
    addSubformFields(state, action) {
      const { updateSubformFields } = action.payload;
      let updateSubformFieldsState = { ...state.subformFields };

      updateSubformFields.forEach((identifier) => {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (!updateSubformFieldsState[identifierWithoutSuffix]) {
          updateSubformFieldsState[identifierWithoutSuffix] = [];
        }

        updateSubformFieldsState[identifierWithoutSuffix] = [
          ...updateSubformFieldsState[identifierWithoutSuffix],
          identifier,
        ];
      });

      state.subformFields = updateSubformFieldsState;
    },
    addSubformFieldsBulk(state, action) {
      const updatedState = { ...state };

      action.payload.forEach(({ identifier }) => {
        const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

        if (!updatedState.subformFields[identifierWithoutSuffix]) {
          updatedState.subformFields[identifierWithoutSuffix] = [];
        }

        updatedState.subformFields[identifierWithoutSuffix] = [
          ...updatedState.subformFields[identifierWithoutSuffix],
          identifier,
        ];
      });

      state = updatedState;
    },
    removeSubformField(state, action) {
      const { identifier } = action.payload;

      const identifierWithoutSuffix = getIdWithoutSuffix(identifier);

      if (state.subformFields[identifierWithoutSuffix]) {
        state.subformFields[identifierWithoutSuffix] = state.subformFields[
          identifierWithoutSuffix
        ].filter((id) => id !== identifier);
      }
    },
    addOrModifyMetadataInput(state, action) {
      const { key, value } = action.payload;
      state.metadataInputs[key] = value;
    },
    removeMetadataInput(state, action) {
      delete state.metadataInputs[action.payload];
    },
    addRunningProcess(state, action) {
      state.runningProcesses = [...state.runningProcesses, action.payload];
    },
    removeRunningProcess(state, action) {
      const indexToDelete = state.runningProcesses.findIndex(
        (process) => process === action.payload
      );

      if (indexToDelete !== -1) state.runningProcesses.splice(indexToDelete, 1);
    },
    addSelectOptionsFromResource(state, action) {
      const { id, options } = action.payload;

      state.selectResourceOptions[id] = options;
    },
    addSelectFieldResource(state, action) {
      const { resourceId, data } = action.payload;

      if (!state.selectFieldResources[resourceId]) {
        state.selectFieldResources[resourceId] = data;
      }
    },
    updateRules(state, action) {
      if (action.payload.value === undefined) {
        delete state.rules[action.payload.identifier];
      } else {
        state.rules[action.payload.identifier] = {
          ...state.rules[action.payload.identifier],
          ...action.payload.value,
        };
      }
    },
    removeInitialAnswers(state, action) {
      const initialAnswersArray = action.payload;
      const updatedState = { ...state };

      initialAnswersArray.forEach((answer) => {
        delete updatedState.initialAnswers[answer.identifier];
      })
    
      state = updatedState;
    },
    updateInitialAnswers(state, action) {
      const initialAnswersArray = action.payload;
      const updatedState = { ...state };

      initialAnswersArray.forEach((answer) => {
        updatedState.initialAnswers[answer.identifier] = answer.value;
      })

      state = updatedState;
    },
    updateFieldErrors(state, action) {
      const newFieldErrors = {
        ...state.fieldErrors,
        ...action.payload.updatedFieldErrors,
      };
      for (const key in newFieldErrors) {
        if (newFieldErrors[key] === undefined) {
          delete newFieldErrors[key];
        }
      }
      state.fieldErrors = newFieldErrors;
    },
    updateFieldErrorBulk(state, action) {
      const updatedState = { ...state };

      if (action.payload.length > 0) {
        action.payload.forEach(({ identifier, errorMessage }) => {
          if (errorMessage === undefined) {
            delete updatedState.fieldErrors[identifier];
          } else {
            updatedState.fieldErrors[identifier] = errorMessage;
          }
        });

        state = updatedState;
      }
    },
    updateFieldResourceUpdateHighlight(state, action) {
      if (action.payload.isHighlighted === undefined) {
        delete state.fieldResourceUpdateHighlights[
          action.payload.fieldIdentifier
        ];
      } else {
        state.fieldResourceUpdateHighlights[action.payload.fieldIdentifier] =
          action.payload.isHighlighted;
      }
    },
    updateFieldsResourceUpdateHighlight(state, action) {
      const fieldsAffectedByUpdate = action.payload.fieldsAffectedByUpdate;
      let currentState = JSON.parse(
        JSON.stringify(state.fieldResourceUpdateHighlights)
      );

      fieldsAffectedByUpdate.forEach((field) => {
        if (action.payload.isHighlighted === undefined) {
          delete currentState[field.identifier];
        } else {
          currentState[field.identifier] = action.payload.isHighlighted;
        }
      });
      
      state.fieldResourceUpdateHighlights = currentState;
    },
    addRequiredResourceLoading(state) {
      state.requiredResourcesLoading += 1;
    },
    removeRequiredResourceLoading(state) {
      state.requiredResourcesLoading -= 1;
    },
    setDraftAnswers(state, action) {
      state.draftAnswers = action.payload;
    },
    removeDraftAnswers(state, action) {
      const { identifier } = action.payload;
      delete state.draftAnswers[identifier];
    },
    setResourceVersions(state, action) {
      state.resourceVersions = action.payload;
    },
    setDispatchAnswers(state, action) {
      state.dispatchAnswers = action.payload;
    },
    removeDispatchAnswer(state, action) {
      delete state.dispatchAnswers[action.payload];
    },
    updateAllRules(state, action) {
      if (Object.keys(action.payload.rules).length > 0) {
        state.rules = { ...state.rules, ...action.payload.rules };
      }
    },
    setRootLevelFields(state, action) {
      const { identifier } = action.payload;
      state.rootLevelFields[identifier] = action.payload;
    },
    removeRootLevelField(state, action) {
      const { identifier } = action.payload;
      delete state.rootLevelFields[identifier];
    },
    setRootLevelFieldsBulk(state, action) {
      const updatedState = { ...state };

      if (action.payload.length > 0) {
        action.payload.forEach((rootField) => {
          updatedState.rootLevelFields[rootField.identifier] = {
            isActive: rootField.isActive,
          };
        });
      }

      state = updatedState;
    },
    setIsLoading(state, action) {
      state.isLoading = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    }
  },
});

export const {
  resetFormSliceState,
  setFieldsWithExpressions,
  setFieldsToWatch,
  clearFieldsToWatch,
  addRepeatGroup,
  addGroupToList,
  removeGroupFromList,
  addRepeatGroupFields,
  addRepeatGroupBulk,
  addSubform,
  removeSubform,
  addSubformField,
  addSubformFieldsBulk,
  addSubformBulk,
  addSubformFields,
  removeSubformField,
  removeRepeatGroupField,
  addOrModifyMetadataInput,
  removeMetadataInput,
  addRunningProcess,
  removeRunningProcess,
  addSelectOptionsFromResource,
  addSelectFieldResource,
  updateRules,
  removeInitialAnswers,
  updateInitialAnswers,
  updateFieldError,
  updateFieldErrorBulk,
  updateFieldErrors,
  updateFieldResourceUpdateHighlight,
  updateFieldsResourceUpdateHighlight,
  addRequiredResourceLoading,
  removeRequiredResourceLoading,
  setDraftAnswers,
  removeDraftAnswers,
  setResourceVersions,
  setDispatchAnswers,
  removeDispatchAnswer,
  updateAllRules,
  setRootLevelFields,
  removeRootLevelField,
  setRootLevelFieldsBulk,
  setIsLoading,
  setIsSubmitting
} = formSlice.actions;
export default formSlice.reducer;

